import { Box, ClickAwayListener, debounce, InputAdornment, TextField, useTheme } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import SearchIcon from '@mui/icons-material/Search';
import { getCreatorsList } from "../../utils/api";
import SearchItem from "./SearchItem";
import Lottie from "lottie-react";
import LoadingFace from '../../assets/animations/LoadingFace.json'
import { drawerWidth, drawerWidthClosed, transitionClose, transitionOpen } from ".";
import { NavContext } from "../../context/NavContext";
import { logging } from "../../utils/logger";

const PAGE_LIMIT = 10;
const INITIAL_SEARCH_RESULTS = {
  results: {},
  isLoading: false,
  fullyLoaded: false,
};

Object.freeze(INITIAL_SEARCH_RESULTS.results)
Object.freeze(INITIAL_SEARCH_RESULTS)

const Search: React.FC = () => {
  const [ searchValue, setSearchValue ] = useState<string>('')
  const [ maxWidth, setMaxWidth ] = useState<string>('0px');
  const searchTimer = useRef<any>(0);
  const bottom = useRef(null);
  // const [ listedUsers, setListedUsers ] = useState({recents: [], suggested: []});
  const [ searchResults, setSearchResults ] = useState(JSON.parse(JSON.stringify(INITIAL_SEARCH_RESULTS)));
  const isSearching = useRef<boolean>(false);
  const { authUser } = useAuth();
  const theme = useTheme();
  const {
    mountedComponent, setMountedComponent,
    showProfileNav,
  } = useContext(NavContext);

  useEffect(() => { // this is for transition
      setMaxWidth(mountedComponent?.name === 'Search' ?
        (showProfileNav ?
          `calc(100% - ${drawerWidth + drawerWidthClosed}px)` :
          `calc(100% - ${drawerWidthClosed}px)`) : '0px')
  }, [mountedComponent, showProfileNav]) //eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //   if (authUser) {
  //     // fetch recent and suggested creators
  //     // console.log('fetch recent and suggested creators')
  //   }
  // }, [authUser])

  useEffect(() => {
    if (searchValue) {
      // search for creators
      if (!searchResults.isLoading) {
        creatorSearch();
      }
    } else {
      // reset
      resetSearch()
    }
  }, [searchValue]) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (searchValue && !searchResults.isLoading && !searchResults.fullyLoaded && !isSearching.current) {
          creatorSearch();
        }
      }
    });

    observer.observe(bottom.current);

    return () => {
      observer.disconnect();
    }
  }, [searchValue, searchResults, isSearching]) //eslint-disable-line react-hooks/exhaustive-deps

  const resetSearch = () => {
    searchTimer.current = 0;
    setSearchValue('');
    setSearchResults(JSON.parse(JSON.stringify(INITIAL_SEARCH_RESULTS)));
  };

  useEffect(() => {
    return () => {
      resetSearch();
    }
  }, [])

  const handleSearch = (event: any) => {
    const newValue = event?.target?.value?.toLowerCase();
    if (searchTimer?.current) {
      clearTimeout(searchTimer.current);
      searchTimer.current = 0;
    }

    searchTimer.current = setTimeout(() => {
      searchTimer.current = 0;
      setSearchValue(newValue);
    }, 250);
  };

  const creatorSearch = () => {
    isSearching.current = true;
    setSearchResults(results => {
      let info = {...results};
      info.isLoading = true;
      return info;
    })

    getCreatorsList(authUser?.uid,
      searchValue,
      (searchResults.results[searchValue]?.["currentPage"] || 0) + 1,
      PAGE_LIMIT
    ).then(res => {
      if (res.ok) {
        return res.json()
      } else {
        throw res
      }
    }).then(data => {
      if (data.data.length === 0) {
        setSearchResults(results => {
          let info = {
            ...results,
            fullyLoaded: true,
            isLoading: false,
          };
          return info;
        })
      } else {
        setSearchResults(results => {
          let info = {
            ...results,
            isLoading: false,
          };
          info.results[searchValue] ||= {};
          info.results[searchValue]["data"] ||= [];
          info.results[searchValue]["data"].push(...data.data);
          info.results[searchValue]["currentPage"] ||= 0;
          info.results[searchValue]["currentPage"]++;
          return info;
        })
      }
    })
    .catch(err => {
      setSearchResults(results => {
        let info = {...results};
        info.isLoading = false;
        return info;
      })
      logging.error('Error in Search: ', {context: {err}})
    })
    .finally(() => {
      isSearching.current = false;
    })
  }

  return (
    <Box
      sx={[{
        position: 'absolute',
        bgcolor: (theme) => theme.palette.divider,
        width: '100%',
        height: '100%',
        zIndex: 501,
        maxWidth: maxWidth,
        [theme.breakpoints.down('md')]: {
          maxWidth: (maxWidth === '0px') ? maxWidth : '100%',
        }
      },
    ]}
    >
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={() => {
          if (mountedComponent?.name === 'Search'){
            setMountedComponent(null);
          }
        }}
      >
        <Box
          width={371}
          height='100%'
          sx={[
            {
              position: 'absolute',
              bgcolor: (theme) => theme.palette.background.default,
              borderRight: (theme) => (maxWidth === '0px') ? 'none' : `1px solid ${theme.palette.divider}`,
              zIndex: 2,
              overflowY: 'auto',
              maxWidth: maxWidth,
              [theme.breakpoints.down('md')]: {
                maxWidth: (maxWidth === '0px') ? maxWidth : '90%',
              }
            },
            (maxWidth === '0px') ? {...transitionClose(theme)} : {...transitionOpen(theme)}
          ]}
          display='flex'
          flexDirection='column'
        >
          <Box
            sx={{
              margin: 2,
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '90%',
              position: 'sticky',
              top: 0,
              zIndex: 3,
              bgcolor: (theme) => theme.palette.background.default,
            }}
          >
            <TextField
              type='text'
              fullWidth
              margin='normal'
              id='text-input-search'
              label='Search'
              placeholder="'Name, username, etc...'"
              onKeyDown={debounce(handleSearch, 500)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }
              }}
              color='primary'
            />
          </Box>

          {
            !!searchValue &&
            !!searchResults.results[searchValue]?.data?.length ?
            searchResults.results[searchValue]?.data?.map((creator, ind) =>
              <SearchItem
                item={creator}
                key={`${creator.creatorId}-${ind}`}
                cleanUp={() => {
                  resetSearch()
                  if (mountedComponent?.name === 'Search'){
                    setMountedComponent(null);
                  }
                }}
              />
            ) :
            <>
              {/* {
                !!listedUsers?.recents.length &&
                <Box>

                </Box>
              }
              {
                !!listedUsers?.suggested.length &&
                <Box>

                </Box>
              } */}
            </>
          }

          <Box ref={bottom} sx={{
            height: '5px',
            width: '100%',
            position: 'relative',
          }}/>
          {
            searchResults.isLoading &&
            <Box
              margin='auto'
              marginTop={1}
              sx={{
                '& path': {
                  fill: (theme) => theme.palette.primary.main
                }
              }}
            >
              <Lottie
                animationData={LoadingFace}
                loop={true}
                style={{
                  width: '100px',
                  height: '100px',
                }}
              />
            </Box>
          }
        </Box>
      </ClickAwayListener>
    </Box>
  )
}

export default Search;
